import * as React from 'react';
import { connect } from 'react-redux';

import {
  Button,
  createStyles,
  Theme,
  WithStyles,
  withStyles
} from '@material-ui/core';
import { Elements } from 'react-stripe-elements';

import { animateScroll } from 'react-scroll';

import classnames from 'classnames';

import {
  ArrowBackRounded,
  FavoriteBorderRounded,
  FavoriteRounded
} from '@material-ui/icons';

import { ILieuucation, ILieuucationTimestamped } from '@lieuu/core';

import * as LieuucationActions from '@actions/LieuucationActions';
import {
  getUser,
  toggleDarkMode,
  toggleFavoritedLieuucation
} from '@actions/UserActions';
import { IStore, IUserState } from '@reducers';
import getFakeLieuucation from '@reducers/getFakeLieuucation';

import Error from '@components/Error';
import FeedbackFab from '@components/FeedbackFab';
import Link from '@components/Link';
import Loading from '@components/Loading';

import LieuucationMap from './LieuucationMap';
import NavigationDrawer from './NavigationDrawer';

import Details from './Details';
import SignUpForm from './SignUpForm';
import VerifyForm from './VerifyForm';

const styles = (theme: Theme) =>
  createStyles({
    actionButtons: {
      position: 'fixed',
      marginLeft: theme.spacing(2),
      marginTop: theme.spacing(2),
      zIndex: theme.zIndex.drawer + 1
    },
    buttonIcon: {
      marginRight: theme.spacing(1)
    },
    actionButton: {
      marginRight: theme.spacing(2)
    },
    container: {},
    rootDetails: {
      display: 'flex',
      flexWrap: 'nowrap',
      flexDirection: 'row-reverse'
    },
    detailsAndMap: {
      position: 'relative'
    },
    map: {
      height: 300,
      [theme.breakpoints.down('sm')]: {
        height: 200
      }
    },
    divider: {
      marginBottom: theme.spacing(2)
    },
    blurred: {
      filter: 'blur(4px)',
      userSelect: 'none'
    },
    overlayBlurred: {
      width: '100%',
      zIndex: 1000,
      position: 'absolute',
      marginLeft: 'auto',
      marginRight: 'auto'
    }
  });

const mapDispatchToProps = {
  toggleDarkMode,
  getUser: getUser.request,
  toggleFavoritedLieuucation: toggleFavoritedLieuucation.request,
  getLieuucationById: LieuucationActions.getLieuucationById.request
};

interface IStoreState {
  lieuucations: ILieuucation[];
  isFetchingLieuucations: boolean;
  viewedLieuucations?: ILieuucationTimestamped[];
  user?: IUserState;
}

type IProps = WithStyles<typeof styles> &
  typeof mapDispatchToProps &
  IStoreState & {
    lieuucationId: string;
    isExampleReport?: boolean;
  };

const BlurredReport: React.FC<WithStyles<typeof styles>> = ({ classes }) => (
  <div className={classes.blurred}>
    <Details lieuucation={getFakeLieuucation()} />
  </div>
);

const LieuucationReport: React.FC<IProps> = ({
  lieuucationId,
  lieuucations,
  isFetchingLieuucations,
  getLieuucationById,
  isExampleReport,
  classes,
  getUser,
  toggleFavoritedLieuucation,
  user
}) => {
  React.useEffect(() => {
    getUser();
  }, []);

  const lieuucation = lieuucations.find(
    (lieuucation) =>
      lieuucation.internalIds && lieuucation.internalIds.includes(lieuucationId)
  );

  React.useEffect(() => {
    animateScroll.scrollToTop();
  }, []);

  React.useEffect(() => {
    if (!isExampleReport && !lieuucation) {
      getLieuucationById({ id: lieuucationId });
    }
  }, [user, lieuucation, lieuucationId, isExampleReport]);

  const centerLat = lieuucation && lieuucation.centLat;
  const centerLon = lieuucation && lieuucation.centLon;

  return (
    <>
      <div className={classes.actionButtons}>
        <Link
          href={
            centerLon && centerLat
              ? `/map?lon=${centerLon}&lat=${centerLat}&zoom=12`
              : `/map`
          }
        >
          <Button
            disabled={isExampleReport}
            variant="contained"
            className={classes.actionButton}
          >
            <ArrowBackRounded className={classes.buttonIcon} />
            Back
          </Button>
        </Link>
        {(isExampleReport || lieuucation) && (
          <Button
            variant="contained"
            className={classes.actionButton}
            disabled={
              isExampleReport ||
              !user ||
              !user.user ||
              (user && user.isFetching)
            }
            onClick={() =>
              user &&
              user.user &&
              toggleFavoritedLieuucation({
                internalId:
                  lieuucation &&
                  lieuucation.internalIds &&
                  lieuucation.internalIds[0],
                user: user.user
              })
            }
          >
            {user &&
            user.user &&
            user.user.favoritedLieuucations &&
            user.user.favoritedLieuucations.some(
              (e) => e.internalId === lieuucationId
            ) ? (
              <FavoriteRounded className={classes.buttonIcon} />
            ) : (
              <FavoriteBorderRounded className={classes.buttonIcon} />
            )}
            Favorite
          </Button>
        )}
      </div>

      <div className={classes.container}>
        <div className={classes.rootDetails}>
          <NavigationDrawer disabled={!lieuucation} />

          <div className={classes.detailsAndMap}>
            <div
              className={classnames(
                classes.map
                // isPrinting && classes.blurred
              )}
            >
              <LieuucationMap
                height="100%"
                lieuucationId={lieuucationId}
                centLon={centerLon || -111.917}
                centLat={centerLat || 33.569}
              />
            </div>

            {isFetchingLieuucations || (user && user.isFetching) ? (
              <>
                <div className={classes.overlayBlurred}>
                  <Loading />
                </div>
                <BlurredReport classes={classes} />
              </>
            ) : lieuucation ? (
              <div>
                <FeedbackFab />
                <Details
                  isExampleReport={Boolean(isExampleReport)}
                  lieuucation={lieuucation}
                />
              </div>
            ) : (!user || !user.user) && !isExampleReport ? (
              <>
                <div className={classes.overlayBlurred}>
                  <Elements>
                    <SignUpForm />
                  </Elements>
                </div>
                <BlurredReport classes={classes} />
              </>
            ) : user && user.user && !user.user.verified ? (
              <>
                <div className={classes.overlayBlurred}>
                  <VerifyForm lieuucationId={lieuucationId} />
                </div>
                <BlurredReport classes={classes} />
              </>
            ) : (
              <Error />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: IStore): IStoreState => ({
  isFetchingLieuucations: Boolean(state.lieuucation.isFetching),
  lieuucations: state.lieuucation.lieuucations,
  viewedLieuucations: state.user.user && state.user.user.viewedLieuucations,
  user: state.user
});

export default withStyles(styles)(
  connect(mapStateToProps, mapDispatchToProps)(LieuucationReport)
);
